import React, { FunctionComponent, useState, useMemo } from 'react';
import { CalendarNav, Eventcalendar, MbscEventcalendarView, MbscResource, Button, CalendarToday, MbscCalendarEventData } from '@mobiscroll/react';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '../../tailwindcomponents';
import { ShoppingCart, Person, Visibility, Soap as SoapIcon } from '../icons';
import { Typography } from './../../tailwindcomponents';
import { Tooltip } from './../../tailwindcomponents/';
import { EventInfo } from '../../types/mobiScroll';
import {DateTime} from 'luxon';
import { formatManHours } from '../../utils/dateUtils';
import { palette } from '../../theme/palette';

type Props = {
    calendarTasks: EventInfo[],
    resources: Array<string>,
    onEventClick: Function,
    isShortTermView: boolean,
    isShopPlanView?: boolean,
}

const MobiscrollCalendar: FunctionComponent<Props> = React.memo(({ calendarTasks, resources, onEventClick, isShortTermView, isShopPlanView }) => {
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [viewType, setViewType] = useState<"week" | "month">("week");

    const view = React.useMemo<MbscEventcalendarView>(() => {
        if (viewType === "week") {
            return {
                timeline: {
                    type: "week",
                    timeCellStep: 240,
                    weekNumbers: true,
                    timeLabelStep: 240,
                    timeLabelFormat: "HH:mm",
                },
            };
        } else {
            return {
                calendar: {
                    type: "month",
                    labels: true,
                },

            };
        }
    }, [viewType]);


    const calendarResources = useMemo<MbscResource[]>(() =>
        [...resources]
            .sort()
            .map(row => ({ id: row, name: row, color: '#e20000' })),
        [resources]);

    const navigateToPrevPage = () => {
        const nextWeek = new Date(currentDate);
        if (viewType === "week") {
            nextWeek.setDate(nextWeek.getDate() - 7);
        }
        else {
            nextWeek.setMonth(nextWeek.getMonth() - 1);
        }
        setCurrentDate(nextWeek);
    };

    const navigateToNextPage = () => {
        const nextWeek = new Date(currentDate);
        if(viewType === "week") {
            nextWeek.setDate(nextWeek.getDate() + 7);
        }
        else {
            nextWeek.setMonth(nextWeek.getMonth() + 1);
        }
        setCurrentDate(nextWeek);
    };

    const navigateToToday = () => {
        const today = new Date();
        setCurrentDate(today);
    };

    const handleViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setViewType(event.target.value as "week" | "month");
    };

    const customHeader = () => {
        return (
            <>
                <CalendarNav />
                <div className="flex flex-wrap items-center gap-2">
                    <Button onClick={navigateToPrevPage} icon="material-arrow-back" color='primary' variant="flat"></Button>
                    <Button onClick={navigateToToday} color='primary' variant="flat"><CalendarToday /></Button>
                    <Button onClick={navigateToNextPage} icon="material-arrow-forward" color='primary' variant="flat"></Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Typography text={"Complete"} align='center' variant='h6' fontWeight="bold" textColor={palette.success} />
                    <Typography text={"ACTV"} align='center' variant='h6' fontWeight="bold" textColor={palette.secondary} />
                    <Typography text={"COMMIT"} align='center' variant='h6' fontWeight="bold" textColor={palette.status.commit} />
                    <Typography text={"IN WORK"} align='center' variant='h6' fontWeight="bold" textColor={palette.status.inwork} />
                    {isShortTermView && <>
                        <SoapIcon color={'primary'} />
                        <Typography text={"Cleaning tasks"} align='center' variant='h6' fontWeight="bold" />
                    </>}
                    {(isShortTermView || isShopPlanView) && <>
                        <ShoppingCart color={'primary'} />
                        <Typography text={"Parts Avail"} align='center' variant='h6' fontWeight="bold" />
                    </>}
                    {(isShortTermView || isShopPlanView) && <>
                        <ShoppingCart color={'error'} />
                        <Typography text={"Parts Not-Avail"} align='center' variant='h6' fontWeight="bold" />
                    </>}
                    {(isShortTermView || isShopPlanView) && <>
                        <Visibility color={'primary'} />
                        <Typography text={"Certification required"} align='center' variant='h6' fontWeight="bold" />
                    </>}
                </div>

                {isShortTermView === false &&
                <div
                    className="flex items-center justify-end">
                        <FormControl>
                            <RadioGroup row value={viewType} onChange={handleViewChange}>
                                <FormControlLabel
                                    value="week"
                                    control={<Radio />}
                                    label="Week"
                                />
                                <FormControlLabel
                                    value="month"
                                    control={<Radio />}
                                    label="Month"
                                />
                            </RadioGroup>
                        </FormControl>
                </div>
                }
            </>
        );
    };

    const getTruncatedTitle = (title: string, startDate: Date, endDate: Date, maxChars: number = 13): string => {
        // Parse dates in case they're strings
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Calculate the duration in hours
        const durationInHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

        // Truncate the title if the event is less than 4 hours
        if (durationInHours <= 4 && title.length > maxChars) {
            return title.slice(0, maxChars) + '...';
        }
        return title;
    };

    const myScheduleEvent = React.useCallback((event: MbscCalendarEventData) => {
        const title = event.title;
        const packet = event.original;
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);
        const toMilTime = (s: string) => DateTime.fromFormat(s, 'dd.MM.yyyy HH:mm').toFormat('HH:mm'); // parsing original string to military time

        if(packet !== undefined) {
            return (
                <div>
                    <div className="flex flex-wrap">
                        <div className="flex flex-wrap" id="icons">
                            {packet.parts_amounts > 0 && (isShortTermView || isShopPlanView) && (
                                <div>
                                    <Tooltip title={`${packet.parts_amounts} parts`} type="primary" placement="top-start">
                                        <ShoppingCart className="cursor-pointer" color={packet.parts_amounts === packet.parts_available ? 'primary' : 'error'} />
                                    </Tooltip>
                                </div>
                            )}
                            {packet.wo_includes_clean_task === 1 && isShortTermView && (
                                <div>
                                    <Tooltip title={`Cleaning task`} type="primary" placement="top-start" >
                                        <SoapIcon className="cursor-pointer" color={'primary'} />
                                    </Tooltip>
                                </div>
                            )}
                            {packet.assigned_employees.length > 0 && (isShortTermView || isShopPlanView) && (
                                <div>
                                    <Tooltip title={`${packet.assigned_employees.length} employees`} type="primary" placement="top-start">
                                        <Person className="cursor-pointer" color="primary" />
                                    </Tooltip>
                                </div>
                            )}
                            {packet.need_cert === true && (isShortTermView || isShopPlanView) && (
                                <div>
                                    <Tooltip title={`Certification required`} type="primary" placement="top-start" >
                                        <Visibility className="cursor-pointer" color={'primary'} />
                                    </Tooltip>
                                </div>
                            )}
                            <p>{packet.need_cert}</p>
                        </div>
                        <div className="flex flex-wrap w-full">
                            <b className="mr-[3px] break-words whitespace-pre-wrap">{getTruncatedTitle(title !== undefined ? title : "", startDate, endDate)}</b>
                            {isShortTermView === false &&
                                <b >Man hours: {formatManHours(packet.mxi_man_hours_days) || 'N/A'}</b>
                            }
                        </div>
                        <div className="w-full">
                            {(isShortTermView || isShopPlanView) &&
                                <p className="mr-[3px] break-words whitespace-pre-wrap">{toMilTime(packet.start_date_text)} - {toMilTime(packet.end_date_text)}</p>
                            }
                            {(isShortTermView || isShopPlanView) === false &&
                                <p className="mr-[3px] break-words whitespace-pre-wrap">{toMilTime(packet.start_date_text)} - {toMilTime(packet.end_date_text)}</p>
                            }
                        </div>
                    </div>
                </div>
            )
        }
        else {
            return (
                <></>
            )
        }
    }, []);

    const renderCalendarEventContent = React.useCallback((event: MbscCalendarEventData ) => {
        const packet = event.original;
        if(packet === undefined) {
            return <></>;
        }
        else{
            return (
                <div>
                <span className="font-bold">
                    {event.title}
                </span>
                {(isShortTermView === false || isShopPlanView) && 
                    <b> Man hours: {formatManHours(packet.mxi_man_hours_days) || 'N/A'}</b>
                }
                {(isShortTermView === false || isShopPlanView) && 
                    <span className="mr-[3px] break-words whitespace-pre-wrap"> {packet.start_date_text} - {packet.end_date_text}</span>
                }
            </div>
            );
        }
    }, []);

    return (
        <div className="h-full min-h-0 flex-1">
            <Eventcalendar
                theme="ios"
                themeVariant="light"
                view={view}
                selectedDate={currentDate}
                data={calendarTasks}
                resources={calendarResources}
                onEventClick={(e: any) => { onEventClick(e) }}
                renderScheduleEventContent={myScheduleEvent} 
                renderLabelContent={renderCalendarEventContent}
                fixedHeader={true}
                renderHeader={customHeader}
                timeFormat="HH:mm"
            />
        </div>
    );
})
export default React.memo(MobiscrollCalendar);